'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { AbstractInput } from '@/ui/form/_shared/AbstractInput.client'
import type { SharedUITextInputProps } from '@/ui/form/_shared/SharedUITextInput.client'
import { SharedUITextInput } from '@/ui/form/_shared/SharedUITextInput.client'
import { DIGITS_ONLY_REGEX } from '@/ui/form/inputs/number/NumberInput.client'

type AccountNumberInputOwnProps = StrictOmit<
  SharedUITextInputProps<'required' | 'asyncValidate'>,
  'placeholder'
> & {
  triggerQuery?: (value: string) => Promise<string | undefined>
  isDisabled?: boolean
}

/**
 * @example
 * ```tsx
 * <Form.Input.AccountNumber
 *  name="account-number"
 *  label="Account number"
 *  required
 *  />
 * ```
 */
export function AccountNumberInput({
  name,
  label,
  required,
  triggerQuery,
  isDisabled,
  ...rules
}: AccountNumberInputOwnProps) {
  const { t } = useTranslation(['accountNumberInput'])

  return (
    <AbstractInput
      {...rules}
      required={required}
      name={name}
      pattern={{
        value: /^\d+$/,
        message: t('accountNumberInput:error.pattern'),
      }}
      asyncValidate={async (value) => {
        if (triggerQuery) {
          return await triggerQuery(value)
        }
      }}
      render={({ field, fieldState, formState }) => (
        <SharedUITextInput
          {...field}
          type="text"
          placeholder={t('accountNumberInput:label')}
          label={label}
          isLoading={fieldState.isValidating}
          isRequired={fieldState.isRequired}
          isSubmitting={formState.isSubmitting}
          error={fieldState.error}
          isDisabled={isDisabled}
          onChange={(e) => {
            field.onChange(e.target.value.replace(DIGITS_ONLY_REGEX, ''))
          }}
        />
      )}
    />
  )
}
